<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { getEnergyDistribution, severityCode } from "@/services/api/energyDistribution.api";
import { energyDistributionColors } from "@/composables/colors";

type DistributionItem = {
  SourceType?: string;
  DisplayValueGas?: number;
  DisplayValueElectra?: number;
  DisplayValueHeat?: number;
  ScaleGas?: number;
  ScaleElectra?: number;
  ScaleHeat?: number;
  DatapointCount?: number;
};

const props = withDefaults(
  defineProps<{
    partyReference?: string | number;
    addressId?: string | number;
    year?: number;
    showModeToggle?: boolean;
    embedded?: boolean;
  }>(),
  {
    year: new Date().getFullYear() - 1,
    showModeToggle: true,
    embedded: false,
  }
);

const isLoading = ref(false);
const hasError = ref(false);
const usagePerSquareMeter = ref(true);
const items = ref<DistributionItem[]>([]);

const segments = [
  { key: "electra", label: "Elektra", unit: "kWh", color: energyDistributionColors.electra, display: "DisplayValueElectra", scale: "ScaleElectra" },
  { key: "gas", label: "Gas", unit: "m³", color: energyDistributionColors.gas, display: "DisplayValueGas", scale: "ScaleGas" },
  { key: "heat", label: "Stadswarmte", unit: "GJ", color: energyDistributionColors.heat, display: "DisplayValueHeat", scale: "ScaleHeat" },
] as const;

const sourceLabels: Record<string, string> = {
  CBS: "Indicatief (CBS)",
  EDS: "Gemeten",
};

const rows = computed(() =>
  items.value.map((item) => {
    return {
      sourceType: sourceLabels[item.SourceType || ""] || item.SourceType || "Onbekend",
      datapointCount: item.DatapointCount,
      segments: segments.map((segment) => ({
        ...segment,
        value: Number(item[segment.display] ?? 0),
        scale: Number(item[segment.scale] ?? 0),
      })),
    };
  })
);

const formatValue = (value: number) => new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 2 }).format(value);

const loadData = async () => {
  if (!props.partyReference || props.partyReference === "undefined" || props.partyReference === "null") {
    items.value = [];
    return;
  }
  if (props.addressId !== undefined && !props.addressId) {
    items.value = [];
    return;
  }
  isLoading.value = true;
  hasError.value = false;
  const response = await getEnergyDistribution(props.partyReference, props.addressId, props.year, usagePerSquareMeter.value);
  if (response?.severity <= severityCode.warning && Array.isArray(response.data)) {
    items.value = response.data;
  } else if (response?.severity <= severityCode.warning && Array.isArray(response.data?.items)) {
    items.value = response.data.items;
  } else {
    items.value = [];
    hasError.value = true;
  }
  isLoading.value = false;
};

watch(() => [props.partyReference, props.addressId, props.year, usagePerSquareMeter.value], loadData, { immediate: true });
</script>

<template>
  <section class="energy-distribution" :class="{ 'energy-distribution--embedded': embedded }" aria-labelledby="energy-distribution-title">
    <v-row class="energy-distribution__header" align="start" justify="space-between" no-gutters>
      <v-col>
        <h2 id="energy-distribution-title">Energie Distributie</h2>
        <p>Gegevens geleverd door Vabi vs CBS</p>
      </v-col>
      <v-col cols="auto" class="widget-actions">
        <information-dropdown>
          <template #header><h2>Energie Distributie</h2></template>
          <p>Verbruiken per m² worden berekend op basis van de totale gebruiksoppervlakte van woningen, vermeerderd met 10% voor algemene ruimten. Waar beschikbaar zijn zowel woningverbruiken als verbruiken van algemene ruimten opgenomen.</p>
        </information-dropdown>
      </v-col>
    </v-row>

    <v-row v-if="showModeToggle" class="energy-distribution__controls" justify="end" no-gutters>
      <v-switch v-model="usagePerSquareMeter" label="Per m²" density="compact" hide-details color="primary" @click.stop />
    </v-row>
    <v-sheet v-if="isLoading" class="energy-distribution__state" color="transparent">Gegevens laden...</v-sheet>
    <v-sheet v-else-if="hasError" class="energy-distribution__state" color="transparent">De energieverdeling kon niet worden opgehaald.</v-sheet>
    <v-sheet v-else-if="!rows.length" class="energy-distribution__state" color="transparent">Geen energieverbruiken beschikbaar.</v-sheet>
    <v-container v-else fluid class="energy-distribution__rows pa-0 d-flex flex-column ga-2 mt-4">
      <v-row v-for="row in rows" :key="row.sourceType" class="energy-distribution__row" no-gutters>
        <v-col cols="12" class="energy-distribution__source">
          <span>{{ row.sourceType }}</span>
          <small v-if="row.datapointCount != null">{{ row.datapointCount }} {{ row.datapointCount === 1 ? "bron" : "bronnen" }}</small>
        </v-col>
        <v-col cols="12" class="energy-distribution__bar-column">
          <v-tooltip location="top">
            <template #activator="{ props: tooltipProps }">
              <v-sheet v-bind="tooltipProps" class="energy-distribution__bar d-flex rounded-lg" color="grey-lighten-4" role="img" :aria-label="`Energieverdeling ${row.sourceType}`">
                <v-sheet v-for="segment in row.segments" :key="segment.key" class="energy-distribution__segment" :style="{ width: `${segment.scale}%`, backgroundColor: segment.color }"></v-sheet>
              </v-sheet>
            </template>
            <v-sheet class="energy-distribution__tooltip-values" color="transparent">
              <v-sheet v-for="segment in row.segments" :key="segment.key" color="transparent">
                {{ segment.label }}: {{ formatValue(segment.value) }} {{ segment.unit }}
              </v-sheet>
            </v-sheet>
          </v-tooltip>
          <v-sheet class="energy-distribution__values" color="transparent" :aria-label="`Waarden voor ${row.sourceType}`">
            <span v-for="segment in row.segments" :key="segment.key">
              <i :style="{ backgroundColor: segment.color }"></i>{{ segment.label }}: {{ formatValue(segment.value) }} {{ segment.unit }}
            </span>
          </v-sheet>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<style scoped lang="scss">
.energy-distribution { height: 100%; min-height: 0; color: #173b55; }
.energy-distribution__header h2 { margin: 0 0 20px 0; font-size: 1.5rem; font-weight: 600; }
.energy-distribution__header p { margin: 0; color: #547084; font-size: 0.88rem; }
.energy-distribution__row { min-width: 0; }
.energy-distribution__source { display: flex; align-items: baseline; gap: 8px; margin-bottom: 5px; font-size: 0.86rem; }
.energy-distribution__source small { color: #7a8d98; font-size: 0.7rem; }
.energy-distribution__controls { z-index: 1; pointer-events: auto; }
.energy-distribution__bar-column { width: 100%; min-width: 0; }
.energy-distribution__bar { width: 100%; min-width: 0; height: 25px; overflow: hidden; }
.energy-distribution__segment { height: 100%; min-width: 0; border-right: 2px solid #fff; transition: width 0.25s ease; }
.energy-distribution__segment:last-child { border-right: 0; }
.energy-distribution__tooltip-values { padding: 4px 8px; font-size: 0.8rem; line-height: 1.4; }
.energy-distribution__values { display: flex; flex-wrap: wrap; gap: 4px 14px; margin-top: 6px; color: #547084; font-size: 0.8rem; line-height: 1.25; }
.energy-distribution__values span { display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
.energy-distribution__values i { width: 10px; height: 10px; flex: 0 0 10px; border-radius: 50%; }
.energy-distribution__state { color: #6d7f89; text-align: center; padding: 28px 0 18px; font-size: 0.88rem; }
@media (max-width: 560px) { .energy-distribution__row { align-items: flex-start; flex-direction: column; gap: 5px; } .energy-distribution__source { width: 100%; flex-basis: auto; } .energy-distribution__bar-column { width: 100%; } .energy-distribution__bar { width: 100%; } }
</style>
